import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Call,
  Communication,
  CommunicationActions,
  CommunicationType,
  CommunicationTypes,
  Email,
  Message,
  Note,
  NoteTypes,
} from 'typings';

import NoteContent from './Note';
import CallContent from './Call';
import SmsContent from './Sms';
import EmailContent from './Email';
import Inappropriate from './Inappropriate';
import Caption from './Caption';

import styled from 'styled-components';
import { apiRemoveIssue, apiReportIssue } from 'state/lead.state';
import ContextMenu from 'components/shared/ContextMenu';

import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

type CommunicationProps = Communication & {
  hasIssue?: boolean;
};

interface Props {
  type: CommunicationType;
  isCensored: boolean;
  isConciergeAdmin: boolean;
  isAdminAction: boolean;
  communication: CommunicationProps;
}

interface StyleProps {
  align?: boolean;
  margin?: boolean;
  textAlign?: boolean;
}

interface RenderProps {
  component: React.ElementType;
  text: string;
  contentWrapper: React.ElementType;
}

const SmsWrapper = styled('div').attrs<StyleProps>((props) => ({}))<StyleProps>`
  display: flex;
  width: fit-content;
  max-width: 75%;
  flex-flow: wrap;
  flex-direction: column;
  align-items: ${(props) => (props.align ? 'flex-start' : 'flex-end')};
  margin-left: ${(props) => (props.margin ? 'initial' : 'auto')};
`;

const EmailWrapper = styled('div').attrs<StyleProps>((props) => ({}))<StyleProps>`
  display: flex;
  width: fit-content;
  max-width: 75%;
  flex-flow: wrap;
  flex-direction: column;
  margin-left: ${(props) => (props.margin ? 'initial' : 'auto')};
`;

const CallWrapper = styled('div').attrs<StyleProps>((props) => ({}))<StyleProps>`
  display: flex;
  width: fit-content;
  align-items: center;
  text-align: ${(props) => (props.textAlign ? 'inherit' : 'right')};
  margin-left: ${(props) => (props.margin ? 'initial' : 'auto')};
`;

const StatusNoteWrapper = styled('div')`
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
`;

const ConsoleNoteWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: fit-content;
  align-items: flex-end;
  justify-content: center;
  margin-left: auto;
`;

const getComponentType = (
  communicationType: CommunicationType,
  communication: Communication
): RenderProps => {
  switch (communicationType) {
    case CommunicationTypes.NOTE:
      const note = communication as Note;
      let NoteWrapper =
        note.type === NoteTypes.QUALIFY ||
        note.type === NoteTypes.UNQUALIFY ||
        note.type === NoteTypes.REOPEN
          ? StatusNoteWrapper
          : ConsoleNoteWrapper;
      return { component: NoteContent, text: note.note, contentWrapper: NoteWrapper };
    case CommunicationTypes.SMS:
      const sms = communication as Message;
      return { component: SmsContent, text: sms.text, contentWrapper: SmsWrapper };
    case CommunicationTypes.CALL:
      return { component: CallContent, text: '', contentWrapper: CallWrapper };
    case CommunicationTypes.EMAIL:
      const email = communication as Email;
      return { component: EmailContent, text: email.text, contentWrapper: EmailWrapper };
    default:
      return { component: 'div', text: '', contentWrapper: 'div' };
  }
};

const showContextMenu = (type: CommunicationType, communication: Communication): boolean => {
  if (type === CommunicationTypes.NOTE || type === CommunicationTypes.CALL) return false;

  if ('incoming' in communication) {
    return !!communication.incoming;
  }
  return false;
};

const applyStyles = (type: CommunicationType, communication: Communication): StyleProps => {
  const styles = {
    margin: false,
    align: false,
    textAlign: false,
  };

  switch (type) {
    case CommunicationTypes.SMS:
      const sms = communication as Message;
      if (sms.incoming) {
        styles.align = true;
        styles.margin = true;
      }
      break;
    case CommunicationTypes.CALL:
      const call = communication as Call;
      if (call.callType === 'inbound') {
        styles.textAlign = true;
        styles.margin = true;
      }
      break;
    case CommunicationTypes.EMAIL:
      const email = communication as Email;
      if (email.incoming) {
        styles.margin = true;
      }
      break;
    default:
      break;
  }

  return styles;
};

const CommunicationWrapper = ({
  type,
  communication,
  isConciergeAdmin,
  isAdminAction,
  isCensored,
}: Props) => {
  const dispatch = useDispatch();
  const [isRevealed, setIsRevealed] = useState(false);
  let {
    component: Content,
    text,
    contentWrapper: ContentWrapper,
  } = getComponentType(type, communication);

  const revealContent = () => {
    setIsRevealed(true);
  };

  const handleContextMenuSelection = (menuItem: any) => {
    let comm;
    switch (type) {
      case CommunicationTypes.EMAIL:
        comm = communication as Email;
        break;
      case CommunicationTypes.SMS:
        comm = communication as Message;
        break;
    }

    if (!comm) return;

    if (menuItem.action === CommunicationActions.REPORT_ISSUE) {
      if (comm.hasIssue) return;
      dispatch(apiReportIssue({ communicationId: comm.id, type }));
    }

    if (menuItem.action === CommunicationActions.REMOVE_ISSUE) {
      if (!comm.hasIssue) return;
      dispatch(apiRemoveIssue({ communicationId: comm.id, type }));
    }
  };

  const buildMenu = (communication: CommunicationProps) => {
    let menuItems = [];

    if (!communication.hasIssue) {
      menuItems.push({
        text: 'Report inappropriate content',
        icon: <VisibilityOffIcon />,
        action: CommunicationActions.REPORT_ISSUE,
      });
    }

    if (isConciergeAdmin && communication.hasIssue) {
      menuItems.push({
        text: 'Mark as safe',
        icon: <VisibilityIcon />,
        action: CommunicationActions.REMOVE_ISSUE,
      });
    }

    return menuItems;
  };

  const styles = applyStyles(type, communication);

  return (
    <ContentWrapper {...styles}>
      <ContextMenu
        menuItems={buildMenu(communication)}
        onSelect={handleContextMenuSelection}
        enabled={showContextMenu(type, communication)}
      >
        <Content
          data={communication}
          isRevealed={isRevealed}
          caption={
            <Caption
              isAdminAction={isAdminAction}
              communication={communication}
              type={type}
              isRevealed={isRevealed}
            />
          }
        >
          {isCensored && !isRevealed ? (
            <Inappropriate
              isRevealed={isConciergeAdmin}
              revealContent={revealContent}
              communicationType={type}
            />
          ) : (
            text
          )}
        </Content>
      </ContextMenu>
    </ContentWrapper>
  );
};

export default CommunicationWrapper;
